import {
  Directive,
  EventEmitter,
  HostBinding,
  Input,
  InjectionToken,
  Output,
  signal
} from '@angular/core';
import { AnimationEvent } from '@angular/animations';

/** Interface satisfied by every overlay component (dialog, bottom-sheet). */
export interface UmssOverlayCloseable {
  handleClose(): void;
}

/**
 * Injection token that overlay components provide so that projected content
 * (e.g. umss-dialog-header) can trigger the animated close without needing
 * a template-reference variable in the parent.
 */
export const UMSS_CLOSE_OVERLAY = new InjectionToken<UmssOverlayCloseable>(
  'UMSS_CLOSE_OVERLAY'
);

/**
 * Base directive that holds all inputs, outputs and interaction logic shared
 * between overlay components (UmssDialogComponent, UmssBottomSheetComponent).
 *
 * Extend this class and add the @Component decorator to create a new overlay.
 */
@Directive()
export abstract class UmssOverlayBaseDirective {
  /**
   * Internal backing field for the `open` input.
   * Never read this directly in templates – use `_visible` instead.
   */
  private _open = false;

  /**
   * Controls the `@if` in the template.
   * Decoupled from the `@Input() open` so that close animations always play,
   * regardless of whether the close was user-initiated or external.
   */
  _visible = false;

  /** True while the close animation is in progress */
  private _closing = false;

  /** True when a close has been initiated and we're waiting for the leave animation */
  private _pendingClose = false;

  /**
   * Whether the overlay is open.
   *
   * The setter detects open↔close transitions and manages `_visible`
   * accordingly. While a close animation is playing (`_closing`), incoming
   * `true` values from the parent binding are ignored so the animation
   * is never interrupted by change-detection pushing the old value back.
   */
  @Input()
  set open(value: boolean) {
    // While animating closed, ignore the parent pushing `true` back
    if (this._closing && value) return;

    const wasOpen = this._open;
    this._open = value;

    if (value && !wasOpen) {
      // closed → open
      this._visible = true;
      this._pendingClose = false;
      this._closing = false;
    } else if (!value && wasOpen && this._visible) {
      // open → closed  (only when overlay is actually visible)
      this._closing = true;
      this._pendingClose = true;
      this._visible = false;
    }
  }
  get open(): boolean {
    return this._open;
  }

  /** Whether the overlay can be closed (X button, etc.) */
  @Input() closable: boolean = true;

  /** Whether to render a semi-transparent backdrop */
  @Input() backdrop: boolean = true;

  /** Whether clicking the backdrop closes the overlay */
  @Input() backdropClosable: boolean = true;

  /** Optional async guard — return false to prevent closing */
  @Input() beforeClose?: () => Promise<boolean> | boolean;

  /** Whether the header slot is in use (controls close-button fallback visibility) */
  @Input() showHeader: boolean = true;

  /** Whether the footer slot is in use */
  @Input() showFooter: boolean = true;

  /** Emitted when the overlay closes (after the leave animation finishes) */
  @Output() onClose = new EventEmitter<void>();

  /** Emitted when the backdrop is clicked */
  @Output() onBackdropClick = new EventEmitter<void>();

  isPointerDownInsideContent = signal(false);

  /**
   * Activates the host overlayHostLeave animation trigger (declared in each
   * concrete component's animations array). The :leave transition delays
   * component destruction so the inner overlay animation always plays,
   * regardless of what caused the parent @if to go false.
   */
  @HostBinding('@overlayHostLeave') readonly _hostLeaveState = 'active';

  /**
   * User-initiated close (backdrop click, header X, etc.).
   * Does NOT mutate the `open` input – instead flips `_visible` to false
   * so the leave animation plays. After the animation, `onClose` is emitted
   * and the parent can update its own state.
   */
  async handleClose() {
    if (!this.closable) return;

    if (this.beforeClose) {
      const result = await this.beforeClose();
      if (!result) return;
    }

    this._closing = true;
    this._pendingClose = true;
    this._visible = false;
  }

  /**
   * Called from the template via (@overlayFadeAnimation.done).
   * Emits onClose after the overlay leave animation completes so that
   * any parent @if wrapper does not destroy the component mid-animation.
   */
  onOverlayAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'void' && this._pendingClose) {
      this._pendingClose = false;
      this._closing = false;
      this.onClose.emit();
    }
  }

  onContentPointerDown(ev: PointerEvent) {
    this.isPointerDownInsideContent.set(true);
    ev.stopPropagation();
  }

  handleBackdropClick(event: MouseEvent) {
    if (event.target !== event.currentTarget) return;
    if (this.isPointerDownInsideContent()) return;

    this.onBackdropClick.emit();

    if (this.backdropClosable) {
      this.handleClose();
    }
  }
}
